import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateQuerySubStatusRequestSchema,
  type CreateQuerySubStatusRequest,
  type QuerySubStatusDetail,
  type QuerySubStatusListItem,
  UpdateQuerySubStatusRequestSchema,
  type UpdateQuerySubStatusRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  useCreateQuerySubStatus,
  useQueryStatusOptions,
  useUpdateQuerySubStatus,
} from '../use-query-sub-statuses';

import { deriveCode, useAutoDeriveCode } from '@/components/master-code-field';
import { StageColorField } from '@/components/StageColorField';

interface QuerySubStatusFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  querySubStatus?: QuerySubStatusDetail | QuerySubStatusListItem | null;
}

const CREATE_DEFAULTS: CreateQuerySubStatusRequest = {
  queryStatusId: '',
  code: '',
  name: '',
  description: '',
  color: null,
  sortOrder: 0,
  isActive: true,
};

/**
 * Field set shared by create + edit. The `queryStatusId` select is driven by
 * the live options list from GET /query-statuses/options - same slim shape used
 * by other sub-entity forms (branches, etc.).
 */
const QuerySubStatusFields = ({
  pending,
  statusOptions,
}: {
  pending: boolean;
  statusOptions: ReadonlyArray<{ value: string; label: string }>;
}): React.JSX.Element => (
  <>
    <UzFormField name="queryStatusId">
      {({ field, error }) => (
        <UzSelect
          label="Query status"
          placeholder="Select parent status"
          value={(field.value as string | undefined) ?? ''}
          onValueChange={field.onChange}
          options={statusOptions}
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Negotiation"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="description">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Description"
          placeholder="Optional"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <StageColorField
      disabled={pending}
      hint="Drives the sub-status chip color. Leave blank to inherit from the parent status."
    />
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive sub statuses are hidden from lead forms."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const QuerySubStatusFormDrawer = ({
  open,
  onOpenChange,
  mode,
  querySubStatus,
}: QuerySubStatusFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createQuerySubStatus = useCreateQuerySubStatus();
  const updateQuerySubStatus = useUpdateQuerySubStatus();
  const pending = createQuerySubStatus.isPending || updateQuerySubStatus.isPending;

  const statusOptionsQuery = useQueryStatusOptions();
  const statusOptions: ReadonlyArray<{ value: string; label: string }> = (
    statusOptionsQuery.data ?? []
  ).map((s) => ({ value: s.id, label: s.name }));

  const createForm = useForm<CreateQuerySubStatusRequest>({
    resolver: zodResolver(CreateQuerySubStatusRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateQuerySubStatusRequest>({
    resolver: zodResolver(UpdateQuerySubStatusRequestSchema),
    defaultValues: {},
  });

  useAutoDeriveCode(createForm, isCreate);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (querySubStatus) {
      // `code` is intentionally omitted - it's the stable machine
      // identifier and is read-only post-create.
      updateForm.reset({
        queryStatusId: querySubStatus.queryStatus.id,
        name: querySubStatus.name,
        description: querySubStatus.description ?? '',
        color: querySubStatus.color ?? null,
        sortOrder: querySubStatus.sortOrder,
        isActive: querySubStatus.isActive,
      });
    }
  }, [open, isCreate, querySubStatus, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    // `code` is derived from the name (hidden field).
    await createQuerySubStatus.mutateAsync({
      ...values,
      code: deriveCode(values.name),
      description: values.description?.trim() ? values.description : null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!querySubStatus) return;
    await updateQuerySubStatus.mutateAsync({
      id: querySubStatus.id,
      payload: {
        ...values,
        description: values.description?.trim() ? values.description : null,
      },
    });
    onOpenChange(false);
  });

  const fields = <QuerySubStatusFields pending={pending} statusOptions={statusOptions} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={
        isCreate ? 'Add query sub status' : `Edit ${querySubStatus?.name ?? 'query sub status'}`
      }
      description="Code is unique within the selected parent status and serves as the stable machine identifier."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
